import { useCallback, useEffect, useMemo } from 'react';

import { MinimalCartEntryType } from 'features/cart/types';
import { useStoreContext } from 'features/store';

import {
  LoyaltyServiceOfferRedemptionType,
  useLoyaltyServiceAnonymousOffersQuery,
  useLoyaltyServiceOffersQuery,
} from 'generated/graphql-gateway';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import {
  ICartEntryType,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import {
  AppOffer,
  AppOfferListItem,
  AppPersonalizedOfferGroup,
  appOfferToLoyaltyOffer,
  isAppOfferGroup,
  isAppPersonalizedOfferGroup,
} from 'state/loyalty/types';
import { useServiceModeLive } from 'state/service-mode';
import noop from 'utils/noop';

import { ServiceOfferItem } from '../types';
import { mapServiceOfferItem } from '../utils';
import { getLoyaltyServiceServiceMode } from '../utils/service-mode';

function isAppOffer(item: AppOfferListItem): item is AppOffer {
  return (
    (item as any).__apptype === 'AppSystemwideOffer' ||
    (item as any).__apptype === 'AppPersonalizedOffer'
  );
}

export function useLoyaltyServiceOffers() {
  const dispatch = useAppDispatch();
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();

  const loyaltyServiceMode = getLoyaltyServiceServiceMode(serviceMode);
  const variables = {
    serviceMode: loyaltyServiceMode,
    storeId,
    redemptionTypes: [LoyaltyServiceOfferRedemptionType.STANDARD],
    includeOfferGroups: true,
    omitInvalids: false,
  };

  const {
    data: loyaltyServiceOffersData,
    loading: loyaltyServiceOffersLoading,
    refetch: refetchLoyaltyServiceOffers,
  } = useLoyaltyServiceOffersQuery({
    variables,
    skip: loyaltyUserIdLoading || !loyaltyUserId,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const {
    data: loyaltyServiceAnonymousOffersData,
    loading: loyaltyServiceAnonymousOffersLoading,
    refetch: refetchLoyaltyServiceAnonymousOffers,
  } = useLoyaltyServiceAnonymousOffersQuery({
    variables,
    skip: loyaltyUserIdLoading || Boolean(loyaltyUserId),
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const loading =
    loyaltyUserIdLoading ||
    (loyaltyUserId ? loyaltyServiceOffersLoading : loyaltyServiceAnonymousOffersLoading);

  const appliedOfferIds = useMemo(() => {
    const ids = new Set(appliedOffers.map(o => o.id ?? ''));
    minimalCartEntries.forEach(entry => {
      if (
        (entry.type === MinimalCartEntryType.discount ||
          entry.type === MinimalCartEntryType.incentive) &&
        entry.referenceId
      ) {
        ids.add(entry.referenceId);
      }
    });
    return ids;
  }, [appliedOffers, minimalCartEntries]);

  const inRestaurantRedemptionIds = useMemo(() => {
    return new Set(
      inRestaurantRedemptionCart
        .filter(entry => entry.type === ICartEntryType.OFFER)
        .map(entry => (entry as any).details?.offer?._id)
        .filter(Boolean)
    );
  }, [inRestaurantRedemptionCart]);

  const isOfferApplied = useCallback(
    (offer: AppOffer) =>
      appliedOfferIds.has(offer.loyaltyEngineId) || inRestaurantRedemptionIds.has(offer.cmsId),
    [appliedOfferIds, inRestaurantRedemptionIds]
  );

  const {
    offerListItems,
    offersInCart,
    personalizedOfferGroups,
    appliedCmsOffers,
  } = useMemo(() => {
    const items = loyaltyUserId
      ? loyaltyServiceOffersData?.loyaltyServiceOffers?.items
      : loyaltyServiceAnonymousOffersData?.loyaltyServiceAnonymousOffers?.items;
    if (!items?.length) {
      return {
        offerListItems: [] as AppOfferListItem[],
        offersInCart: [] as AppOffer[],
        personalizedOfferGroups: [] as AppPersonalizedOfferGroup[],
        appliedCmsOffers: [] as AppOffer[],
      };
    }

    const mapped = items.map(item => mapServiceOfferItem(item as ServiceOfferItem));
    const list: AppOfferListItem[] = [];
    const cart: AppOffer[] = [];
    const pGroups: AppPersonalizedOfferGroup[] = [];
    // Only offers actually applied to the cart, never the full browsable
    // catalog: `setCmsOffers` merges cumulatively and never evicts an entry
    // (see offers.slice.ts), so caching every returned offer here would let a
    // redeemed/expired personalized offer linger and defeat the cart-cleanup
    // check in state/order/index.tsx that relies on a missing cmsOffers entry
    // to remove a stale applied offer.
    const applied: AppOffer[] = [];

    for (const item of mapped) {
      if (isAppOffer(item)) {
        if (isOfferApplied(item)) {
          cart.push(item);
          applied.push(item);
        } else {
          list.push(item);
        }
      } else {
        list.push(item);
        if (isAppPersonalizedOfferGroup(item) || isAppOfferGroup(item)) {
          applied.push(...item.offers.filter(isOfferApplied));
        }
        if (isAppPersonalizedOfferGroup(item)) {
          pGroups.push(item);
        }
      }
    }

    return {
      offerListItems: list,
      offersInCart: cart,
      personalizedOfferGroups: pGroups,
      appliedCmsOffers: applied,
    };
  }, [isOfferApplied, loyaltyServiceAnonymousOffersData, loyaltyServiceOffersData, loyaltyUserId]);

  // The cart's discount-offer lookup (selectDiscountAppliedCmsOffer) reads from the
  // legacy `cmsOffers` slice, which only the pre-loyalty-service offers hook otherwise
  // populates. Without this, offers applied via the loyalty-service flow are recorded
  // in `appliedOffers` but never surface in the cart. See BKPE-27876.
  useEffect(() => {
    if (appliedCmsOffers.length) {
      dispatch(actions.loyalty.setCmsOffers(appliedCmsOffers.map(appOfferToLoyaltyOffer)));
    }
  }, [appliedCmsOffers, dispatch]);

  const refreshOffers = useCallback(() => {
    if (loyaltyUserIdLoading) {
      return;
    }
    if (loyaltyUserId) {
      refetchLoyaltyServiceOffers();
      return;
    }
    refetchLoyaltyServiceAnonymousOffers();
  }, [
    loyaltyUserId,
    loyaltyUserIdLoading,
    refetchLoyaltyServiceAnonymousOffers,
    refetchLoyaltyServiceOffers,
  ]);

  const reprocessOffers = useCallback(noop, []);

  return {
    offerListItems,
    offersInCart,
    personalizedOfferGroups,
    reprocessOffers,
    refreshOffers,
    loading,
    baseOffersLoading: loading,
  };
}
